import React from 'react';
import { IHTCalculation, EstateAssets, EstateProfile, RiskSeverity } from '../../types';
import { assessProbateRisks } from '../../server/services/probateRiskDetector';
import { ShieldAlert, ShieldCheck, AlertTriangle, ChevronRight } from 'lucide-react';

interface Props {
 ihtCalc?: IHTCalculation | null;
 assets?: EstateAssets;
 profile?: EstateProfile;
}

const LEVEL_CONFIG: Record<RiskSeverity, {
 bg: string; border: string; text: string; badge: string;
 Icon: React.FC<{ className?: string }>;
 label: string;
}> = {
 low: {
 bg: 'bg-white', border: 'border-[#E8E4DB]', text: 'text-[#1A1A1A]',
 badge: 'bg-white text-[#1A1A1A] border-[#E8E4DB]',
 Icon: ShieldCheck, label: 'Low Risk',
 },
 medium: {
 bg: 'bg-[#FAF3E0]', border: 'border-[#E8D89A]', text: 'text-[#5A4200]',
 badge: 'bg-[#FAF3E0] text-[#7A5A00] border-[#E8D89A]',
 Icon: AlertTriangle, label: 'Medium Risk',
 },
 high: {
 bg: 'bg-white', border: 'border-[#E8E4DB]', text: 'text-[#1A1A1A]',
 badge: 'bg-white text-[#1A1A1A] border-[#1A1A1A]',
 Icon: ShieldAlert, label: 'High Risk',
 },
};

const SEVERITY_BADGE: Record<RiskSeverity, string> = {
 low: 'bg-white text-[#1A1A1A] border border-[#E8E4DB]',
 medium: 'bg-[#FAF3E0] text-[#7A5A00] border border-[#E8D89A]',
 high: 'bg-white text-[#1A1A1A] border border-[#1A1A1A]',
};

const MAX_VISIBLE = 5;

export const ProbateRiskOverview: React.FC<Props> = ({ ihtCalc, assets, profile }) => {
 const assessment = assessProbateRisks(ihtCalc, assets, profile);
 const [expanded, setExpanded] = React.useState(false);

 if (assessment.risks.length === 0) {
 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 <div className="bg-[#1A1A1A] px-6 py-4">
 <div className="flex items-center gap-2">
 <ShieldCheck className="w-4 h-4 text-[#3730A3]" />
 <h2 className="text-xl font-bold text-white">Probate Risk Overview</h2>
 </div>
 <p className="text-[#AFA99F] text-sm mt-1">No significant risks detected.</p>
 </div>
 <div className="p-6">
 <p className="text-sm text-[#3F3D39]">
 Based on the information provided, no issues were identified that would typically delay or complicate estate administration.
 </p>
 </div>
 </div>
 );
 }

 const level = LEVEL_CONFIG[assessment.overall_risk_level];
 const LevelIcon = level.Icon;
 const visibleRisks = expanded ? assessment.risks : assessment.risks.slice(0, MAX_VISIBLE);
 const hiddenCount = assessment.risks.length - MAX_VISIBLE;

 return (
 <div className="bg-white border border-[#E8E4DB] rounded overflow-hidden">
 {/* Header with overall risk level */}
 <div className="bg-[#1A1A1A] px-6 py-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <ShieldAlert className="w-4 h-4 text-[#7A5A00]" />
 <h2 className="text-xl font-bold text-white">Probate Risk Overview</h2>
 </div>
 <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border ${level.badge}`}>
 <LevelIcon className="w-3 h-3 inline mr-1 -mt-0.5" />
 {level.label}
 </span>
 </div>
 <p className="text-[#AFA99F] text-sm mt-1">
 {assessment.risks.length} risk{assessment.risks.length !== 1 ? 's' : ''} identified that may affect estate administration.
 </p>
 </div>

 {/* Risk list */}
 <div className="p-6 space-y-3">
 {visibleRisks.map(risk => {
 const sev = LEVEL_CONFIG[risk.severity];
 const SevIcon = sev.Icon;
 return (
 <div key={risk.id} className={`rounded-lg border ${sev.bg} ${sev.border} p-3`}>
 <div className="flex items-start gap-2.5">
 <SevIcon className={`w-4 h-4 mt-0.5 shrink-0 ${sev.text}`} />
 <div className="flex-1 min-w-0">
 <p className={`text-sm font-bold ${sev.text}`}>{risk.name}</p>
 <p className="text-xs text-[#3F3D39] mt-0.5 leading-relaxed">{risk.why}</p>
 <div className="mt-2 flex items-start gap-1.5">
 <ChevronRight className="w-3 h-3 mt-0.5 shrink-0 text-[#AFA99F]" />
 <p className="text-xs font-medium text-[#3F3D39]">{risk.action}</p>
 </div>
 </div>
 <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${SEVERITY_BADGE[risk.severity]} whitespace-nowrap shrink-0 ml-auto`}>
 {risk.severity}
 </span>
 </div>
 </div>
 );
 })}

 {/* Show more / less toggle */}
 {hiddenCount > 0 && (
 <button
 onClick={() => setExpanded(!expanded)}
 className="w-full text-center text-xs font-medium text-[#3730A3] hover:text-[#3730A3] py-2 hover:underline transition-colors"
 >
 {expanded ? 'Show fewer risks' : `Show ${hiddenCount} more risk${hiddenCount !== 1 ? 's' : ''}...`}
 </button>
 )}
 </div>
 </div>
 );
};
